import { Button } from "@/components/ui/button";
import { Clock, Check, Plus, Loader2, Users, ClipboardList, Timer, GraduationCap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
interface ClassCardProps {
  class_: {
    id: string;
    title: string;
    description: string;
    instructor: string;
    schedule: string;
    capacity: number;
    duration: number;
    enrolledCount: number;
  };
  isSubscribed: boolean;
  onAction: () => void;
  isLoading: boolean;
  userRole?: string;
  viewMode?: string;
  currentUserInstructorName?: string;
  index?: number;
}
export const ClassCard = ({
  class_,
  isSubscribed,
  onAction,
  isLoading,
  userRole,
  viewMode,
  currentUserInstructorName,
  index = 0
}: ClassCardProps) => {
  const navigate = useNavigate();
  const isFull = class_.enrolledCount >= class_.capacity;
  const isTeacherOrManager = userRole === 'profesor';
  const isClassInstructor = currentUserInstructorName && class_.instructor === currentUserInstructorName;
  const showManageButton = isTeacherOrManager && isClassInstructor && (viewMode === 'created' || viewMode === 'all');
  const spotsLeft = class_.capacity - class_.enrolledCount;
  const formatSchedule = (schedule: string) => {
    if (/^\d{2}:\d{2}$/.test(schedule)) {
      return schedule;
    }
    const hour = parseInt(schedule);
    if (!isNaN(hour)) {
      return `${hour.toString().padStart(2, '0')}:00`;
    }
    return schedule;
  };
  const handleManageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/gestion-asistencia/${class_.id}`);
  };

  // Stagger animation delay based on index
  const staggerClass = index < 5 ? `stagger-${index + 1}` : '';
  return <div className={`glass-card p-5 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 animate-fade-in ${staggerClass} group relative overflow-hidden`}>
      {/* Background gradient on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {/* Status badge */}
      <div className="absolute top-4 right-4 z-10">
        {isFull ? <Badge variant="destructive" className="text-xs font-medium">
            Llena
          </Badge> : <Badge variant="secondary" className="text-xs font-medium bg-primary/20 text-primary border-primary/30">
            {spotsLeft} {spotsLeft === 1 ? 'lugar' : 'lugares'}
          </Badge>}
      </div>

      <div className="relative z-10">
        {/* Header with icon and title */}
        <div className="flex items-start gap-4 mb-4">
          <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors duration-300 shrink-0">
            <GraduationCap className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0 pr-16">
            <h3 className="text-lg font-semibold text-foreground truncate group-hover:text-primary transition-colors">
              {class_.title}
            </h3>
            <p className="text-sm text-muted-foreground">
              {class_.instructor}
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {class_.description}
        </p>

        {/* Stats chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
            <Clock className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-medium text-foreground">{formatSchedule(class_.schedule)}</span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
            <Timer className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-medium text-foreground">{class_.duration} min</span>
          </div>
          
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          {(!isFull || isSubscribed) && <Button variant={isSubscribed ? "secondary" : "default"} size="sm" onClick={onAction} disabled={isLoading || isFull && !isSubscribed} className="flex-1 group-hover:shadow-md transition-all">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : isSubscribed ? <>
                  <Check className="w-4 h-4 mr-2" />
                  Inscrito
                </> : <>
                  <Plus className="w-4 h-4 mr-2" />
                  Inscribirse
                </>}
            </Button>}
          
          {showManageButton && <Button variant="outline" size="sm" onClick={handleManageClick} className="shrink-0 border-primary/30 hover:bg-primary/10" title="Gestionar asistencia">
              <ClipboardList className="w-4 h-4 text-primary" />
            </Button>}
        </div>
      </div>
    </div>;
};